const API_URL = process.env.REACT_APP_API_URL;
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '';
const API_SEARCH_ENDPOINT_NAME =
  process.env.REACT_APP_API_SEARCH_ENDPOINT_NAME || '/search-images';
const API_GROUP_ENDPOINT_NAME =
  process.env.REACT_APP_API_GROUP_ENDPOINT_NAME || '/images/group';
const API_LOGIN_ENDPOINT_NAME =
  process.env.REACT_APP_API_LOGIN_ENDPOINT_NAME || '/login';

const buildUrl = (endpoint, params = {}) => {
  const query = new URLSearchParams(params).toString();
  return `${API_URL}${API_PREFIX}${endpoint}${query ? `?${query}` : ''}`;
};

const readJsonSafe = async (res) => {
  try {
    return await res.json();
  } catch {
    return {};
  }
};

export const getCurrentFolders = () => {
  try {
    return JSON.parse(localStorage.getItem('folders') || '{}');
  } catch {
    return {};
  }
};

export const getToken = () => localStorage.getItem('token');
export const getAccountKey = () => localStorage.getItem('accountKey');

export const clearAuthData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('folders');
  localStorage.removeItem('accountKey');

  window.dispatchEvent(new Event('folders-updated'));
};

export const saveAuthData = ({ token, folders, accountKey }) => {
  clearAuthData();

  if (token) {
    localStorage.setItem('token', token);
  }

  localStorage.setItem(
    'folders',
    JSON.stringify(folders && typeof folders === 'object' ? folders : {})
  );

  localStorage.setItem('accountKey', accountKey || '');

  window.dispatchEvent(new Event('folders-updated'));
};

export const logout = () => {
  clearAuthData();
};

export const login = async ({ login, password }) => {
  clearAuthData();

  const res = await fetch(buildUrl(API_LOGIN_ENDPOINT_NAME), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ login, password }),
  });

  const data = await readJsonSafe(res);

  if (!res.ok) {
    throw new Error(data?.detail || 'Ошибка входа');
  }

  saveAuthData({
    token: data?.token,
    folders: data?.folders || {},
    accountKey: data?.accountKey || '',
  });

  return data;
};

export const fetchMe = async () => {
  const token = getToken();

  if (!token) {
    throw new Error('Токен отсутствует');
  }

  const res = await fetch(buildUrl('/me'), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await readJsonSafe(res);

  if (!res.ok) {
    throw new Error(data?.detail || 'Не удалось получить профиль');
  }

  saveAuthData({
    token,
    folders: data?.folders || {},
    accountKey: data?.accountKey || '',
  });

  return data;
};

const normalizeImage = (item, idx = 0) => {
  const payload = item?.payload ?? item ?? {};

  const id =
    payload.id ??
    payload.image_id ??
    payload.fileId ??
    payload.file_id ??
    payload.point_id ??
    idx;

  return {
    ...payload,
    id,
    groupId: payload.groupId ?? payload.group_id ?? id,
    groupSize: payload.groupSize ?? payload.group_size ?? 1,
    isGroupCover: payload.isGroupCover ?? payload.is_group_cover ?? true,
    thumbnailLink: payload.thumbnailLink ?? payload.thumbnail_link ?? '',
    fileLink: payload.fileLink ?? payload.file_link ?? '',
    createdTime: payload.createdTime ?? payload.created_time ?? '',
    width: payload.width ?? '',
    height: payload.height ?? '',
    size: payload.size ?? 0,
    parentPath: payload.parentPath ?? payload.parent_path ?? '',
    fullPath: payload.fullPath ?? payload.full_path ?? '',
  };
};

const resolveFolderName = (folderValue) => {
  const folders = getCurrentFolders();

  if (!folderValue) return '';

  const folderName = Object.keys(folders).find(
    (name) => folders[name] === folderValue
  );

  return folderName || folderValue;
};

const buildSearchParams = (filters = {}) => {
  const params = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (key === 'folder') {
        const folderName = resolveFolderName(value);

        if (folderName) {
          params[key] = folderName;
        }
      } else {
        params[key] = value;
      }
    }
  });

  return params;
};

export const fetchImages = async (filters) => {
  try {
    const params = buildSearchParams(filters);
    const token = getToken();
    const url = buildUrl(API_SEARCH_ENDPOINT_NAME, params);

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await readJsonSafe(res);

    if (!res.ok) {
      throw new Error(data?.detail || `HTTP error! status: ${res.status}`);
    }

    const imagesArray = data?.message?.result || [];
    return imagesArray.map((item, idx) => normalizeImage(item, idx));
  } catch (err) {
    console.error('Failed to fetch images:', err);
    return [];
  }
};

export const fetchImagesByImage = async (file, filters = {}) => {
  try {
    if (!file) {
      throw new Error('Файл не выбран');
    }

    const params = buildSearchParams(filters);
    const token = getToken();
    const url = buildUrl('/search-images-by-image', params);

    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await readJsonSafe(res);

    if (!res.ok) {
      throw new Error(data?.detail || `HTTP error! status: ${res.status}`);
    }

    const imagesArray = data?.message?.result || [];
    return imagesArray.map((item, idx) => normalizeImage(item, idx));
  } catch (err) {
    console.error('Failed to fetch images by image:', err);
    throw err;
  }
};

export const fetchGroupImages = async (groupId, folder) => {
  try {
    if (!API_GROUP_ENDPOINT_NAME) {
      console.warn('REACT_APP_API_GROUP_ENDPOINT_NAME is not set');
      return [];
    }

    const folderName = resolveFolderName(folder);

    if (!folderName) {
      console.warn('Folder is required for fetchGroupImages');
      return [];
    }

    const token = getToken();
    const url = buildUrl(API_GROUP_ENDPOINT_NAME, {
      group_id: groupId,
      folder: folderName,
      limit: 200,
    });

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await readJsonSafe(res);

    if (!res.ok) {
      throw new Error(data?.detail || `HTTP error! status: ${res.status}`);
    }

    const imagesArray =
      data?.message?.result?.points ||
      data?.message?.result ||
      data?.message?.items ||
      data?.result?.points ||
      data?.result ||
      data?.items ||
      [];

    return imagesArray.map((item, idx) => normalizeImage(item, idx));
  } catch (err) {
    console.error('Failed to fetch group images:', err);
    return [];
  }
};